import { create, type StateCreator } from "zustand";

import LANGUAGES from "@config/i18n/langs.json";
import { zustandMiddlware } from "@core/stores/middleware";

import i18n from "../propai-i18n";

const defaultLanguage = "en";

type State = {
  currentLanguage: string;
  selectedLanguage: string;
};

type Action = {
  updateLanguage: (newLanguage: string) => void;
  confirmLanguage: () => void;
  languageOptions: Map<string, string>;
  resetLanguage: () => void;
};

type LanguageStore = State & Action;

const languageStore: StateCreator<
  LanguageStore,
  [["zustand/devtools", never]],
  [],
  LanguageStore
> = (set, get) => {
  const storedLanguage = localStorage.getItem("language") || defaultLanguage;
  (async () => {
    await i18n.changeLanguage(storedLanguage);
  })();

  const isDev = import.meta.env.DEV;
  const baseLanguages = LANGUAGES as [string, string][];
  const extraLanguages: [string, string][] = [["km", "Khmer"]];
  return {
    currentLanguage: storedLanguage,
    selectedLanguage: storedLanguage,
    languageOptions: new Map(isDev ? [...baseLanguages, ...extraLanguages] : baseLanguages),

    // Update language selection
    updateLanguage: (newLanguage) => set({ selectedLanguage: newLanguage }),

    // Confirm language selection
    confirmLanguage: () => {
      const { selectedLanguage } = get();
      set({ currentLanguage: selectedLanguage });
      localStorage.setItem("language", selectedLanguage);
    },

    // Reset/Cancel
    resetLanguage: () => {
      const { currentLanguage } = get();
      set({ selectedLanguage: currentLanguage });
    },
  };
};

export default create<LanguageStore>()(
  zustandMiddlware(languageStore, {
    name: "languageStore",
  })
);
